import { Component, Inject } from '@angular/core';
import { context } from 'bookingbug-core-js';
import { AppConfig } from './app.config';
import { TranslateService } from '@ngx-translate/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { DatastoreService } from './services/datastore.service';
import { AlertService } from './_alert';
import { filter } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { ApiService } from './services/jrni/api.service';
import { BasketService } from './services/jrni/basket.service';

import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus, RedirectRequest } from '@azure/msal-browser';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent {
  loadIssue: boolean;

  constructor(
    private translate: TranslateService,
    private router: Router,
    private DataStore: DatastoreService,
    private alertService: AlertService,
    private route: ActivatedRoute,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private titleService: Title,
    private apiService: ApiService,
    private basketService: BasketService
  ) {
    translate.addLangs(['en', 'cy']);
    translate.setDefaultLang('en');
    translate.use('en');

    this.DataStore.notify.subscribe((result) => {
      this.loadIssue = result;
      this.scrollToTop();
      this.alertService.clear();
      this.alertService.error(this.translate.instant('COMMON.LOAD_ISSUE'));
    });
  }

  ngOnInit() {
    // RWRSPI-967: Recover from the browser Back button being pressed on the
    // HeyCentric gateway. The gateway's own Back/Cancel buttons return to the
    // /payment-verify page (handled there), but the browser Back button pops
    // history to the page we left (summary) — frequently restored frozen from
    // the bfcache with the "redirecting" spinner still showing, so the user is
    // stuck. pageshow fires on bfcache restore; the direct call covers a fresh
    // (non-bfcache) reload where ngOnInit runs again.
    window.addEventListener('pageshow', this.recoverFromAbandonedPayment);
    this.recoverFromAbandonedPayment();

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const url = event.urlAfterRedirects || event.url;
        const lastSegment = url.split('/').filter(Boolean).pop();
        const formatted = lastSegment.replace(/[-_]/g, ' ')
          .replace(/\b\w/g, char => char.toUpperCase());
        this.titleService.setTitle(`${formatted} | Westminster City Council`);

        if (!url.includes('start')) {
          setTimeout(() => {
            const main = document.getElementById('main-content');
            main?.focus();
          }, 0);
        }
      });


    // BB Context storage
    console.log(AppConfig.settings);
    context.apiUrl = AppConfig.settings.apiServer.api_url;
    context.apiVersion = AppConfig.settings.apiServer.version;

    console.log(window.location.pathname);

    this.route.queryParams.subscribe((params: any) => {
      for (const param of Object.keys(params)) {
        console.log(param);
        if (param === 'admin-only' && params[param]) {
          this.DataStore.adminJourney = true;
          console.log('This is an admin journey');
        }
        if (param === 'auth_token' && params[param]) {
          console.log('Auth token', params[param]);
          this.DataStore.adminAuthToken = params[param];
          localStorage.setItem('auth_token', params[param]);
          context.isAdmin = true;
        }
        if (param === 'citizenship' && params[param]) {
          console.log('citizenship journey');
          this.DataStore.isCitizenshipRoute = true;
          this.DataStore.updateCitizenship(true);
          this.apiService.setCitizenshipToken();
        }
      }
    });

    const prevJourney = sessionStorage.getItem('journey');
    if (window.location.search.indexOf('auth_token') === -1) {
      if (!window.location.pathname.includes('verify')) {
        if (prevJourney && !window.location.pathname.startsWith("/start")) {
          console.log(`returning to: /start/${prevJourney}`);
          window.location.href = `/start/${prevJourney}`;
        }
      }
    }

    // if these exist then go to the ?admin=true journey
    // make sure we arent on the admin journey already
    if (window.location.search.indexOf('auth_token') === -1) {
      if (window.location.pathname.indexOf('verify') === -1) {
        if (window.location.pathname.indexOf('citizenship') === -1) {
          if (localStorage.getItem('auth_token') && localStorage.getItem('agent') && !this.DataStore.adminJourney) {
            console.log('Admin journey detected from localStorage');
            this.DataStore.adminJourney = true;
            this.DataStore.adminAuthToken = localStorage.getItem('auth_token');
            context.isAdmin = true;
            // naviage to /services?admin-only=true
            let redirUrl = `/services?admin-only=true&auth_token=${this.DataStore.adminAuthToken}`;


            if (window.location.search.indexOf('citizenship') !== -1) {
              redirUrl = `${redirUrl}&citizenship=true`;
            }
            window.location.assign(redirUrl);
          }
        }
      }
    }

  }

  // RWRSPI-967: If a HeyCentric payment was in flight (flag set by the summary
  // page before it redirected to the gateway) and we are now showing a non-verify
  // page again, the user pressed the browser Back button on the gateway. Clear the
  // basket they abandoned and restart the journey on the correct landing page.
  // The flag value is the booking company id so the basket can be cleared without
  // relying on in-memory journey state (which is lost on a hard reload).
  recoverFromAbandonedPayment = async () => {
    const pendingCompanyId = sessionStorage.getItem('heycentric_payment_pending');
    if (!pendingCompanyId) {
      return;
    }
    // The gateway redirects forward to a /payment-verify (or certificate /verify)
    // route on success or its own Back/Cancel buttons; that page resets the
    // journey itself, so ignore any verify route here.
    if (window.location.pathname.includes('verify')) {
      return;
    }
    // Resolve the destination BEFORE the await: other components' ngOnInit can
    // run during the await and change auth_token / journey state out from under us.
    const authToken: string = localStorage.getItem('auth_token');
    const isAgent = !!localStorage.getItem('agent') && !!authToken;
    const journey = sessionStorage.getItem('journey');
    const destination = isAgent
      // Agent/telephony journey: return to the admin services page.
      ? `/services?admin-only=true&auth_token=${authToken}`
      // Public journey: return to the landing page for the journey they were on.
      : (journey ? `/start/${journey}` : `/start/default`);
    sessionStorage.removeItem('heycentric_payment_pending');
    try {
      await this.apiService.deleteBaskets({ id: pendingCompanyId });
    } catch (err) {
      console.warn('Could not clear basket after payment back-press:', err);
    }
    this.basketService.currentBasket = null;
    window.location.assign(destination);
  };

  skipToMainContent(event: Event) {
    event.preventDefault(); // Prevent default anchor behavior
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.focus();
    }
  }

  scrollToTop() {
    const scrollToTop = window.setInterval(() => {
      const pos = window.pageYOffset;
      if (pos > 0) {
        const scrollToOptions = { top: 0, left: 0, behavior: 'instant' };
        window.scrollTo(scrollToOptions as unknown as ScrollToOptions);
      } else {
        window.clearInterval(scrollToTop);
      }
    }, 10);
  }
}
